// Presentation-only terms for the active asset's commercial-offer PDF.
// Saved per asset, but never simulation inputs. Only send the API allowlist.
import { assetTypeDefinition } from "../../utils/projectAssets";

export const COMMERCIAL_OFFER_SECTION_ID = "commercial-offer";

// A regulatory proposal is one integrated hybrid plant, never a PV-only offer.
export const isRegulatoryCommercialMode = (mode) => ["11A", "11B"].includes(mode);
export function regulatoryCommercialOfferMode(config) {
  const asset = assetTypeDefinition(config?.systemType);
  const mode = config?.arbitrageMerchant?.regulatoryFramework?.mode;
  return config?.settlementType === "Arbitrage" && Boolean(asset?.hasPv && asset?.hasBess)
    && isRegulatoryCommercialMode(mode) ? mode : undefined;
}
export const isRegulatory11AOffer = (config) => regulatoryCommercialOfferMode(config) === "11A";

// Project proposals have one integrated package. Merchant is a commercial
// presentation mode, not an additional regulatory category: "merchant" is a
// PV + BESS plant, "merchant_bess" a standalone battery (no PV, no wind).
export const isMerchantCommercialMode = (mode) => mode === "merchant" || mode === "merchant_bess";
export const isProjectCommercialMode = (mode) => isRegulatoryCommercialMode(mode) || isMerchantCommercialMode(mode);
export function commercialProjectOfferMode(config) {
  const regulatory = regulatoryCommercialOfferMode(config);
  if (regulatory) return regulatory;
  const asset = assetTypeDefinition(config?.systemType);
  const mode = config?.arbitrageMerchant?.regulatoryFramework?.mode;
  if (config?.settlementType !== "Arbitrage" || !(mode == null || mode === "none")) return undefined;
  if (asset?.hasPv && asset?.hasBess) return "merchant";
  if (asset?.hasBess && !asset?.hasPv && !asset?.hasWind) return "merchant_bess";
  return undefined;
}

export const COMMERCIAL_OFFER_LIMITS = {
  reference: 60, recipient: 120, valid_until: 10,
  scope: 220, payment: 220, notes: 220,
};

const EMPTY_PRICE = { materials: null, vat_recoverable_pct: 0 };
export function defaultCommercialHeaderTitle(reportLanguage = "en") {
  return reportLanguage === "el"
    ? "Τεχνική & οικονομική προσφορά"
    : "Technical & commercial proposal";
}
export function defaultCommercialBenefitText(reportLanguage = "en", regulatoryMode) {
  // Identical to the backend's BENEFIT_MERCHANT_BESS_* so a stored default is
  // recognised there and follows the study.
  if (regulatoryMode === "merchant_bess") return reportLanguage === "el"
    ? "Αξιοποιήστε τις διαφορές τιμών στις επιλεγμένες αγορές ηλεκτρικής ενέργειας με έναν σταθμό αποθήκευσης: η μπαταρία φορτίζει στις ώρες χαμηλών τιμών και εγχέει στις ώρες υψηλών τιμών, σύμφωνα με τις ρυθμίσεις και τα αποτελέσματα της μελέτης."
    : "Use price spreads in the selected electricity markets with an energy storage plant: the battery charges when prices are low and injects when they are high, following the completed study's settings and results.";
  if (regulatoryMode === "merchant") return reportLanguage === "el"
    ? "Συνδυάστε φωτοβολταϊκή παραγωγή και αποθήκευση σε έναν ενιαίο σταθμό. Αξιοποιήστε την παραγωγή και τις διαφορές τιμών στις επιλεγμένες αγορές ηλεκτρικής ενέργειας, με λειτουργία της μπαταρίας σύμφωνα με τις ρυθμίσεις και τα αποτελέσματα της μελέτης."
    : "Combine solar generation and storage in one plant. Use generation and price spreads in the selected electricity markets, with battery operation following the completed study's settings and results.";
  if (regulatoryMode === "11B") return reportLanguage === "el"
    ? "Συνδυάστε φωτοβολταϊκή παραγωγή και αποθήκευση σε έναν ενιαίο σταθμό. Αξιοποιήστε την ενέργεια Φ/Β και τη λειτουργία της μπαταρίας στις επιλεγμένες αγορές ηλεκτρικής ενέργειας, με βάση τα αποτελέσματα της μελέτης."
    : "Combine solar generation and storage in one plant. Use PV energy and battery operation in the selected electricity markets, based on the completed study.";
  if (regulatoryMode === "11A") return reportLanguage === "el"
    ? "Συνδυάστε φωτοβολταϊκή παραγωγή και αποθήκευση σε έναν ενιαίο σταθμό. Αξιοποιήστε την Τιμή Αναφοράς για την ενέργεια Φ/Β και τις ευκαιρίες της αγοράς επόμενης ημέρας για την ενέργεια της μπαταρίας, με βάση τα αποτελέσματα της μελέτης."
    : "Combine solar generation and storage in one plant. Use the Reference Price for PV energy and day-ahead market opportunities for battery energy, based on the completed study.";
  return reportLanguage === "el"
    ? "Μειώστε την εξάρτηση από το δίκτυο, αποκτήστε καλύτερο έλεγχο του κόστους και επιλέξτε τη λύση που ταιριάζει καλύτερα στον τρόπο που καταναλώνετε ενέργεια."
    : "Reduce your reliance on the grid, gain better control of costs and choose the solution that best fits the way you use energy.";
}
export function defaultCommercialVatNote(vatPct, reportLanguage = "en") {
  const pct = String(vatPct);
  return reportLanguage === "el"
    ? `Οι συνολικές τιμές περιλαμβάνουν ΦΠΑ ${pct}%.`
    : `Total prices include VAT ${pct}%.`;
}
// Recognise only our exact generated wording, not a user's custom clarification.
const AUTOMATIC_VAT_NOTE = /^(?:Οι συνολικές τιμές περιλαμβάνουν ΦΠΑ|Total prices include VAT) \d+(?:[.,]\d+)?%\.$/;
export const MAX_COVER_IMAGE_CHARS = 2 * 1024 * 1024 * 4 / 3 + 100;
export const PROPOSAL_LIMITS = { offer_date: 10, cover_image_name: 200, summary_image_name: 200, back_image_name: 200,
  summary_title: 100, benefit_text: 600, site_image_name: 200, site_image_caption: 180, title: 100, header_title: 100, company_address: 180, company_tax_id: 40,
  company_website: 120, introduction: 900, company_profile: 600, delivery: 220,
  installation: 220, warranties: 600 };
// Optional look and structure of the offer. Every default is the document
// as it was before these options: blank colour/font/heading, no custom steps,
// all pages included.
export const DEFAULT_BRAND_COLOR = "#0b887c";
export const OFFER_COLOR_PRESETS = [
  { value: "", swatch: DEFAULT_BRAND_COLOR, el: "Τιρκουάζ (προεπιλογή)", en: "Teal (default)" },
  { value: "#1f5fae", swatch: "#1f5fae", el: "Μπλε", en: "Blue" },
  { value: "#183a6b", swatch: "#183a6b", el: "Σκούρο μπλε", en: "Dark blue" },
  { value: "#2e7d32", swatch: "#2e7d32", el: "Πράσινο", en: "Green" },
  { value: "#c8641e", swatch: "#c8641e", el: "Πορτοκαλί", en: "Orange" },
  { value: "#b4442f", swatch: "#b4442f", el: "Κεραμιδί", en: "Terracotta" },
  { value: "#8e2440", swatch: "#8e2440", el: "Μπορντό", en: "Burgundy" },
  { value: "#5e4a9c", swatch: "#5e4a9c", el: "Μωβ", en: "Purple" },
  { value: "#37474f", swatch: "#37474f", el: "Γραφίτης", en: "Graphite" },
  { value: "#a67c00", swatch: "#a67c00", el: "Χρυσό", en: "Gold" },
];
export const OFFER_FONTS = [
  { value: "", label: "Liberation Sans", el: "προεπιλογή", en: "default", css: "\"Liberation Sans\", Arial, sans-serif" },
  { value: "noto_sans", label: "Noto Sans", el: "καθαρή, ευανάγνωστη", en: "clean, highly legible", css: "\"Noto Sans\", sans-serif" },
  { value: "source_sans", label: "Source Sans 3", el: "κομψή, εταιρική", en: "elegant, corporate", css: "\"Source Sans 3\", sans-serif" },
  { value: "ibm_plex_sans", label: "IBM Plex Sans", el: "τεχνική, σύγχρονη", en: "technical, modern", css: "\"IBM Plex Sans\", sans-serif" },
  { value: "noto_serif", label: "Noto Serif", el: "κλασική, με πατούρες", en: "classic serif", css: "\"Noto Serif\", serif" },
];
export const OPTIONAL_PROPOSAL_PAGES = [
  { key: "summary", el: "Η πρότασή μας με μια ματιά", en: "Proposal at a glance" },
  { key: "options", el: "Επιλογές συστήματος", en: "System options" },
  { key: "energy", el: "Πώς η ενέργεια γίνεται όφελος", en: "How energy becomes a benefit" },
  { key: "cost_chart", el: "Μηνιαίο ενεργειακό κόστος", en: "Monthly energy cost" },
  { key: "equipment", el: "Εξοπλισμός", en: "Equipment" },
  { key: "implementation", el: "Βήματα υλοποίησης & εγκατάσταση", en: "Implementation steps & site" },
  { key: "monthly_chart", el: "Μηνιαία ενέργεια", en: "Monthly energy" },
];
export const PROPOSAL_STEP_LIMITS = { count: 6, title: 40, text: 160 };
export const COVER_HEADING_LIMIT = 90;
export function defaultCoverHeading(reportLanguage = "en") {
  return reportLanguage === "el" ? "Η ενεργειακή λύση για τη δική σας εγκατάσταση" : "The energy solution for your installation";
}
// The four steps the proposal shows when no own steps are set.
export function defaultProposalSteps(reportLanguage = "en") {
  return reportLanguage === "el" ? [
    { title: "Συμφωνία", text: "Επιλογή λύσης και συμφωνία εμπορικών όρων." },
    { title: "Προετοιμασία", text: "Οριστικοποίηση σχεδιασμού και απαιτούμενων διαδικασιών." },
    { title: "Εγκατάσταση", text: "Προμήθεια εξοπλισμού και εγκατάσταση του συστήματος." },
    { title: "Παράδοση", text: "Έλεγχος λειτουργίας και παράδοση στον πελάτη." },
  ] : [
    { title: "Agreement", text: "Select the solution and agree commercial terms." },
    { title: "Preparation", text: "Finalise design and required procedures." },
    { title: "Installation", text: "Supply equipment and install the system." },
    { title: "Handover", text: "Check operation and hand over to the customer." },
  ];
}
export const TAX_ASSUMPTIONS = ["assumptions_confirmed", "natural_person", "eligible_property_right",
  "greek_property_and_supplier", "no_subsidy", "no_business_deduction",
  "electronic_payment_and_invoice", "eligibility_assumed"];
export const DEFAULT_COMMERCIAL_OFFER = {
  ...Object.fromEntries(Object.keys(COMMERCIAL_OFFER_LIMITS).map((key) => [key, ""])),
  pricing: { vat_pct: 24, vat_note: defaultCommercialVatNote(24), main: { ...EMPTY_PRICE }, include_pv_only: false, pv_only: { ...EMPTY_PRICE },
    show_cost_breakdown: false },
  proposal: { ...Object.fromEntries(Object.keys(PROPOSAL_LIMITS).map((key) => [key, ""])), header_title: defaultCommercialHeaderTitle(), benefit_text: defaultCommercialBenefitText(), show_acceptance: false, cover_image: "", summary_image: "", back_image: "", site_image: "", equipment: [],
    brand_color: "", font: "", cover_heading: "", steps: [], hidden_pages: [] },
  tax_relief: { enabled: false, payment_year: 2026, basis: "gross", remaining_cap: 16000,
    ...Object.fromEntries(TAX_ASSUMPTIONS.map((key) => [key, false])),
    main: { goods: null, services: null }, pv_only: { goods: null, services: null }, annual_tax_capacity: [null, null, null, null, null] },
};

const text = (value, max) => typeof value === "string" ? value.slice(0, max) : "";
// Keep invalid active values for validation; never silently turn them into zero.
const numeric = (value, fallback = null) => value == null || value === "" ? fallback
  : Number.isFinite(Number(value)) ? Number(value) : value;
const price = (value, vatPct) => {
  const materials = numeric(value?.materials);
  // Older offers stored an independent price/grant and a shared price basis.
  // Drop those overrides, but preserve a goods allocation in its new net basis.
  const netMaterials = value?.basis === "gross" && typeof materials === "number"
    && materials >= 0 && materials <= 1e9
    && typeof vatPct === "number" && vatPct >= 0 && vatPct <= 100
    ? Math.round((materials / (1 + vatPct / 100) + Number.EPSILON) * 100) / 100 : materials;
  return { materials: netMaterials, vat_recoverable_pct: numeric(value?.vat_recoverable_pct, 0) };
};

export function normalizeCommercialOffer(value, { systemType, reportLanguage = "en", regulatoryMode } = {}) {
  const terms = Object.fromEntries(Object.entries(COMMERCIAL_OFFER_LIMITS).map(([key, max]) => [
    key, typeof value?.[key] === "string" ? value[key].slice(0, max) : "",
  ]));
  const p = value?.pricing, detail = value?.proposal, tax = value?.tax_relief;
  const vatPct = numeric(p?.vat_pct, 24);
  const storedNote = text(p?.vat_note, 180);
  const vatNote = !storedNote.trim() || AUTOMATIC_VAT_NOTE.test(storedNote.trim())
    ? defaultCommercialVatNote(vatPct, reportLanguage) : storedNote;
  const asset = assetTypeDefinition(systemType);
  const hybridPv = Boolean(asset?.hasPv && asset?.hasBess);
  const includePvOnly = !isProjectCommercialMode(regulatoryMode) && (systemType == null || hybridPv)
    && (typeof p?.include_pv_only === "boolean" ? p.include_pv_only : hybridPv);
  const storedBenefitText = text(detail?.benefit_text, PROPOSAL_LIMITS.benefit_text);
  const benefitText = !storedBenefitText.trim()
    || [defaultCommercialBenefitText("el"), defaultCommercialBenefitText("en"),
      defaultCommercialBenefitText("el", "11A"), defaultCommercialBenefitText("en", "11A"),
      defaultCommercialBenefitText("el", "11B"), defaultCommercialBenefitText("en", "11B"),
      defaultCommercialBenefitText("el", "merchant"), defaultCommercialBenefitText("en", "merchant"),
      defaultCommercialBenefitText("el", "merchant_bess"), defaultCommercialBenefitText("en", "merchant_bess")].includes(storedBenefitText.trim())
    ? defaultCommercialBenefitText(reportLanguage, regulatoryMode) : storedBenefitText;
  const storedHeaderTitle = text(detail?.header_title, PROPOSAL_LIMITS.header_title);
  const headerTitle = !storedHeaderTitle.trim()
    || [defaultCommercialHeaderTitle("el"), defaultCommercialHeaderTitle("en")].includes(storedHeaderTitle.trim())
    ? defaultCommercialHeaderTitle(reportLanguage) : storedHeaderTitle;
  return { ...terms,
    pricing: { vat_pct: vatPct, vat_note: vatNote,
      main: price(p?.main, vatPct), include_pv_only: includePvOnly, pv_only: price(p?.pv_only, vatPct),
      // Optional page listing the net price item by item (VAT on the total).
      show_cost_breakdown: p?.show_cost_breakdown === true },
    proposal: { ...Object.fromEntries(Object.entries(PROPOSAL_LIMITS).map(([key, max]) => [key, text(detail?.[key], max)])),
      header_title: headerTitle,
      benefit_text: benefitText,
      show_acceptance: detail?.show_acceptance === true,
      cover_image: typeof detail?.cover_image === "string" ? detail.cover_image : "",
      summary_image: typeof detail?.summary_image === "string" ? detail.summary_image : "",
      back_image: typeof detail?.back_image === "string" ? detail.back_image : "",
      site_image: typeof detail?.site_image === "string" ? detail.site_image : "",
      equipment: (Array.isArray(detail?.equipment) ? detail.equipment : []).slice(0, 24).map((item) => ({
        description: text(item?.description, 180), brand: text(item?.brand, 180),
        quantity: text(item?.quantity, 40), capacity: text(item?.capacity, 80), warranty: text(item?.warranty, 120),
        option: ["main", "pv_only"].includes(item?.option) ? item.option : "both",
      })),
      // Kept as typed so a colour can be entered; validation checks the format.
      brand_color: text(detail?.brand_color, 7).trim().toLowerCase(),
      font: OFFER_FONTS.some((f) => f.value === detail?.font) ? detail.font : "",
      cover_heading: text(detail?.cover_heading, COVER_HEADING_LIMIT),
      steps: (Array.isArray(detail?.steps) ? detail.steps : []).slice(0, PROPOSAL_STEP_LIMITS.count).map((step) => ({
        title: text(step?.title, PROPOSAL_STEP_LIMITS.title), text: text(step?.text, PROPOSAL_STEP_LIMITS.text),
      })),
      hidden_pages: OPTIONAL_PROPOSAL_PAGES.map((page) => page.key)
        .filter((key) => Array.isArray(detail?.hidden_pages) && detail.hidden_pages.includes(key)),
    },
    tax_relief: { enabled: !isProjectCommercialMode(regulatoryMode) && tax?.enabled === true, payment_year: numeric(tax?.payment_year, 2026),
      basis: tax?.basis === "net" ? "net" : "gross", remaining_cap: numeric(tax?.remaining_cap, 16000),
      ...Object.fromEntries(TAX_ASSUMPTIONS.map((key) => [key, tax?.[key] === true])),
      main: { goods: numeric(tax?.main?.goods), services: numeric(tax?.main?.services) },
      pv_only: { goods: numeric(tax?.pv_only?.goods), services: numeric(tax?.pv_only?.services) },
      annual_tax_capacity: Array.from({ length: 5 }, (_, i) => numeric(tax?.annual_tax_capacity?.[i])),
    },
  };
}

export function commercialOfferValidation(value, { isGreek = false } = {}) {
  const t = normalizeCommercialOffer(value), p = t.pricing;
  const tr = (el, en) => isGreek ? el : en;
  for (const key of ["cover_image", "site_image", "summary_image", "back_image"]) {
    const image = t.proposal[key];
    if (image && (image.length > MAX_COVER_IMAGE_CHARS || !/^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/.test(image))) {
      const names = { cover_image: tr("εξωφύλλου", "cover"), site_image: tr("εγκατάστασης", "installation"),
        summary_image: tr("σύνοψης", "summary"), back_image: tr("οπισθοφύλλου", "back-cover") };
      return tr(`Επιλέξτε έγκυρη εικόνα ${names[key]} PNG ή JPEG, έως 2 MB.`,
        `Choose a valid PNG or JPEG ${names[key]} image up to 2 MB.`);
    }
  }
  const offerDate = t.proposal.offer_date;
  if (offerDate && (!/^\d{4}-\d{2}-\d{2}$/.test(offerDate) || !Number.isFinite(Date.parse(offerDate))
    || new Date(offerDate).toISOString().slice(0, 10) !== offerDate)) {
    return tr("Ελέγξτε την ημερομηνία προσφοράς.", "Check the offer date.");
  }
  if (t.proposal.brand_color && !/^#[0-9a-f]{6}$/.test(t.proposal.brand_color)) {
    return tr("Ελέγξτε το χρώμα της προσφοράς: μορφή #RRGGBB, π.χ. #1f5fae.", "Check the offer colour: format #RRGGBB, e.g. #1f5fae.");
  }
  const valid = (v, max = 1e9) => v == null || (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= max);
  if (!valid(p.vat_pct, 100)) return tr("Ο ΦΠΑ πρέπει να είναι από 0% έως 100%.", "VAT must be between 0% and 100%.");
  for (const option of [p.main, p.pv_only]) {
    if (!valid(option.materials) || !valid(option.vat_recoverable_pct, 100)) {
      return tr("Ελέγξτε την αξία υλικών και το ποσοστό ανάκτησης ΦΠΑ.", "Check goods allocation and recoverable VAT values.");
    }
  }
  const tax = t.tax_relief;
  if (!valid(tax.remaining_cap, 16000) || !Number.isInteger(tax.payment_year) || tax.payment_year < 2000 || tax.payment_year > 2100
    || !tax.annual_tax_capacity.every((v) => valid(v))
    || ![tax.main.goods, tax.main.services, tax.pv_only.goods, tax.pv_only.services].every((v) => valid(v))) {
    return tr("Ελέγξτε το έτος πληρωμής, το υπόλοιπο ορίου και τις μη αρνητικές φορολογικές τιμές.", "Check payment year, remaining ceiling and non-negative tax inputs.");
  }
  return "";
}

// The request body for an offer PDF. Options left at their defaults are not
// sent, so an offer without them is exactly the request made before they
// existed, and the server renders today's document.
export function offerTermsForRequest(terms) {
  if (terms?.pricing && terms.pricing.show_cost_breakdown !== true && "show_cost_breakdown" in terms.pricing) {
    const pricing = { ...terms.pricing };
    delete pricing.show_cost_breakdown;
    terms = { ...terms, pricing };
  }
  if (!terms?.proposal) return terms;
  const proposal = { ...terms.proposal };
  if (!proposal.brand_color || proposal.brand_color === DEFAULT_BRAND_COLOR) delete proposal.brand_color;
  if (!proposal.font) delete proposal.font;
  if (!String(proposal.cover_heading || "").trim()) delete proposal.cover_heading;
  if (!(proposal.steps || []).some((step) => step.title.trim() || step.text.trim())) delete proposal.steps;
  if (!(proposal.hidden_pages || []).length) delete proposal.hidden_pages;
  return { ...terms, proposal };
}
